import axios from "axios";
import React, { useEffect, useState } from "react";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@material-ui/core";

import Loader from "./Loader";
import TableHeadCell from "./TableHeadCell";
import DeviceBridgedIpForm from "./DeviceBridgedIPForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

const BRIDGED_IP_FIELDS = {
  id: "ID",
  bridged_ip_rule_id: "Bridged IP Rule ID",
  status: "Status",
  server: "Server",
  inbound_ip: "Inbound IP",
  subnet: "Subnet",
  gateway: "Gateway",
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "25%",
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

const DeviceBridgedIP = (props) => {
  const classes = useStyles();
  const [rules, setRules] = useState(null);
  const deviceSerial = props.deviceSerial || "UNIT12345";
  const [loading, setLoading] = useState(false);
  const { setError, setMessage } = props;
  const [openForm, setOpenForm] = useState(null);
  const [availableIps, setAvailableIps] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  const getRules = (deviceSerial) => {
    setLoading(true);
    axios
      .get(`/device/bridged_ip/get_rules/${deviceSerial}`)
      .then((response) => {
        setRules(response.data);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error fetching rules");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteRule = (deviceSerial, ruleId) => {
    setLoading(true);
    axios
      .post(`/device/bridged_ip/delete_rule/${deviceSerial}/${ruleId}`)
      .then((response) => {
        setMessage("Deleted rule successfully");
        getRules(deviceSerial);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error deleting rule");
      })
      .finally(() => {
        getAvailableIps();
      });
  };

  useEffect(() => {
    getRules(deviceSerial);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getAvailableIps();
    // eslint-disable-next-line
  }, []);

  const getAvailableIps = () => {
    setLoading(true);

    axios
      .get(`/device/port_forward/get_available_ips?ip_type=dedicated`)
      .then((response) => {
        setAvailableIps(response.data);
      })
      .catch((error) => {
        setError("Error fetching available ips");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getSummary = (rule) => {
    return (
      <>
        <Typography
          className={classes.heading}
        >{`Inbound IP: ${rule.inbound_ip}`}</Typography>
        <Typography
          className={classes.heading}
        >{`Status: ${rule.status}`}</Typography>
      </>
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedRuleId(null);
        }}
        onConfirm={() => {
          deleteRule(deviceSerial, selectedRuleId);
          setDeleteDialogOpen(false);
        }}
      />
      <Box>
        <Grid item>
          <Button
            onClick={() => setOpenForm(true)}
            color="primary"
            variant="contained"
            style={{ marginBottom: "5px" }}
          >
            Create New Rule
          </Button>
        </Grid>
        {openForm && (
          <DeviceBridgedIpForm
            deviceSerial={deviceSerial}
            setError={setError}
            setMessage={setMessage}
            open={openForm}
            handleOpen={setOpenForm}
            availableIps={availableIps}
            callRules={getRules}
            callback={getAvailableIps}
          />
        )}
        {rules?.bridged_ips &&
          rules?.bridged_ips.map((rule) => (
            <Accordion key={rule.id}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="content"
                id={rule.id}
              >
                {getSummary(rule)}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRuleId(rule.id);
                    setDeleteDialogOpen(true);
                  }}
                  style={{
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                  }}
                  variant="contained"
                  startIcon={<DeleteIcon />}
                >
                  Delete Rule
                </Button>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table aria-label="Bridged IP table">
                    <TableBody>
                      {Object.keys(BRIDGED_IP_FIELDS).map((field) => (
                        <TableRow key={`${rule.id}${field}`}>
                          <TableHeadCell>{BRIDGED_IP_FIELDS[field]}</TableHeadCell>
                          <TableCell>{rule[field] || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    </>
  );
};

export default DeviceBridgedIP;
